/**
 * RiskApplicationModal — 3-step MUI Stepper (Phase 8)
 *
 * Steps:
 *   0 — Exposicao  (exposureLevel, startDate, endDate, observations)
 *   1 — Tipos de Exame  (examTypes checkboxes)
 *   2 — Inclusoes em Relatorios  (reportTypes checkboxes)
 *
 * @version 2025.2.0
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  ExposureLevel,
} from '../../types/riskApplication';
import type {
  RiskApplicationScope,
  RiskApplicationExistingEpiRow,
  RiskApplicationMeasurementRow,
} from '../../types/riskApplication';
import type { ExamType, ReportType } from '../../types/occupationalRisk';
// ExamType / ReportType used for casting on submit (string[] form → typed arrays)
import {
  RISK_MATRIX_TYPES,
  getMaxProbability,
  getMaxSeverity,
  pickCompleteMatrix,
} from '../../types/riskMatrix';
import type { RiskMatrixType } from '../../types/riskMatrix';
import { riskApplicationService } from '../../services/riskApplicationService';
import { occupationalRiskService } from '../../services/occupationalRiskService';
import { riskProtectionService } from '../../services/riskProtectionService';
import { Step1Exposure } from './steps/Step1Exposure';
import { Step2ExamTypes } from './steps/Step2ExamTypes';
import { Step3Reports } from './steps/Step3Reports';
import { ControlMeasuresStep } from './steps/ControlMeasuresStep';
import type { ControlMeasuresEpiOption } from './steps/ControlMeasuresStep';
import { MeasurementsStep } from './steps/MeasurementsStep';
import type { MeasurementEquipmentOption } from './steps/MeasurementsStep';
import { RiskMatrixStep } from '../../pages/occupational-risks/components/steps/RiskMatrixStep';
import { measurementEquipmentService } from '../../services/measurementEquipmentService';
import { MeasurementEquipmentStatus } from '../../types/measurementEquipment';

// ============================================================================
// TYPES
// ============================================================================

export interface MeasurementFormRow {
  measurementEquipmentId: number | null;
  /** Nulo na periculosidade: a NR-16 enquadra a atividade em vez de medir (#424) */
  value: number | null;
  unit: string;
  measuredAt: Date | null;
  observations: string;
  storedQuantity: number | null;
  storedQuantityUnit: string;
  transportedQuantity: number | null;
  transportedQuantityUnit: string;
  riskArea: string;
  storageType: string;
  hazardousFraming: boolean;
  hazardousAnnex: string;
  unhealthyFraming: boolean;
  unhealthyDegreeApplied: string;
}

export interface StepperFormValues {
  exposureLevel: ExposureLevel;
  startDate: Date | null;
  endDate: Date | null;
  observations: string;
  /** Risk matrix override (inherited default from the parent risk). */
  riskMatrixType: RiskMatrixType | null;
  riskProbability: number | null;
  riskSeverity: number | null;
  /** string[] at form level; cast to ExamType[] on submit */
  examTypes: string[];
  /** string[] at form level; cast to ReportType[] on submit */
  reportTypes: string[];
  existingAdministrativeMeasures: string[];
  existingEngineeringMeasures: string[];
  existingAdministrativeOther: string;
  existingEngineeringOther: string;
  existingEpiIds: number[];
  proposedAdministrative: string;
  proposedEngineering: string;
  proposedEpi: string;
  measurements: MeasurementFormRow[];
}

export interface RiskApplicationModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the page can refetch. */
  onSaved: () => void;
  scope: RiskApplicationScope;
  scopeId: number;
  mode: 'apply' | 'edit';
  /** riskId — required for apply mode */
  riskId?: number;
  /** applicationId — required for edit mode */
  applicationId?: number;
  /** Risk display name shown in the title */
  riskName?: string;
  /** Risk factor name shown as a chip in the title */
  riskFactorName?: string;
}

// ============================================================================
// STEPS CONFIG
// ============================================================================

const STEPS = ['Exposicao', 'Tipos de Exame', 'Inclusoes em Relatorios', 'Medidas de Controle', 'Medicoes'];

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

/**
 * O valor medido deixa de ser exigido quando o risco é de periculosidade — a natureza
 * do risco só é conhecida em tempo de execução e chega ao schema pelo contexto do
 * react-hook-form (#424).
 */
function isHazardousContext(context: unknown): boolean {
  return (context as { isHazardousRisk?: boolean } | undefined)?.isHazardousRisk === true;
}

// yup infers string[] for array schemas; we cast to StepperFormValues at usage.
/**
 * "Não pode ser no futuro", conferido **na hora de validar** e não na carga do módulo.
 *
 * `.max(new Date(), ...)` parecia a mesma coisa e não era: o esquema é montado uma vez, quando o
 * módulo carrega, então aquele `new Date()` virava um teto congelado no instante da carga da
 * página. O valor padrão do campo é o instante em que a caixa abre — sempre depois. Bastavam
 * segundos de aba aberta para o campo reprovar o próprio valor padrão, e a pessoa ficava presa
 * no primeiro passo sem ter digitado nada.
 */
const NAO_E_FUTURO = 'nao-e-futuro';
const aindaNaoAconteceu = (valor: Date | null | undefined): boolean =>
  !valor || valor.getTime() <= Date.now();

/** Exportado para o teste: é o que prova que o teto de data não congela na carga do módulo. */
export const validationSchema = yup.object({
  exposureLevel: yup
    .mixed<ExposureLevel>()
    .oneOf(Object.values(ExposureLevel), 'Nivel de exposicao invalido')
    .required('Nivel de exposicao e obrigatorio')
    .defined(),
  startDate: yup
    .date()
    .nullable()
    .defined()
    .test(NAO_E_FUTURO, 'Data de inicio nao pode ser no futuro', aindaNaoAconteceu),
  endDate: yup
    .date()
    .nullable()
    .defined()
    .when('startDate', {
      is: (val: Date | null) => val !== null,
      then: (schema) =>
        schema.min(
          yup.ref('startDate'),
          'Data de fim deve ser posterior a data de inicio'
        ),
    }),
  observations: yup
    .string()
    .defined()
    .max(2000, 'Observacoes nao podem exceder 2000 caracteres'),
  riskMatrixType: yup
    .mixed<RiskMatrixType>()
    .nullable()
    .defined()
    .oneOf([...RISK_MATRIX_TYPES, null], 'Tipo de matriz invalido'),
  riskProbability: yup
    .number()
    .nullable()
    .defined()
    .transform((value, original) => (original === '' ? null : value))
    .when('riskMatrixType', {
      is: (t: RiskMatrixType | null) => t != null,
      then: (schema) =>
        schema.required('Informe a probabilidade').test(
          'prob-range',
          'Probabilidade fora da faixa da matriz',
          function (value) {
            const type = this.parent.riskMatrixType as RiskMatrixType | null;
            if (value == null || type == null) return true;
            return Number.isInteger(value) && value >= 1 && value <= getMaxProbability(type);
          },
        ),
    }),
  riskSeverity: yup
    .number()
    .nullable()
    .defined()
    .transform((value, original) => (original === '' ? null : value))
    .when('riskMatrixType', {
      is: (t: RiskMatrixType | null) => t != null,
      then: (schema) =>
        schema.required('Informe a severidade').test(
          'sev-range',
          'Severidade fora da faixa da matriz',
          function (value) {
            const type = this.parent.riskMatrixType as RiskMatrixType | null;
            if (value == null || type == null) return true;
            return Number.isInteger(value) && value >= 1 && value <= getMaxSeverity(type);
          },
        ),
    }),
  examTypes: yup.array().of(yup.string().required()).defined().default([]),
  reportTypes: yup.array().of(yup.string().required()).defined().default([]),
  existingAdministrativeMeasures: yup.array().of(yup.string().required()).defined().default([]),
  existingEngineeringMeasures: yup.array().of(yup.string().required()).defined().default([]),
  existingAdministrativeOther: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  existingEngineeringOther: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  existingEpiIds: yup.array().of(yup.number().required()).defined().default([]),
  proposedAdministrative: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  proposedEngineering: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  proposedEpi: yup.string().defined().max(2000, 'Máximo 2000 caracteres'),
  measurements: yup.array().of(yup.object({
    measurementEquipmentId: yup.number().nullable().defined(),
    value: yup.number().nullable().defined().transform((v, o) => (o === '' ? null : v))
      .test('value-required', 'Informe o valor', function (v) {
        return v != null || isHazardousContext(this.options.context);
      })
      .test('value-positive', 'Valor deve ser maior que zero', (v) => v == null || v > 0),
    unit: yup.string().defined().max(50),
    measuredAt: yup.date().nullable().defined()
      .test('date-required', 'Informe a data', (v) => v != null)
      .test(NAO_E_FUTURO, 'Data não pode ser no futuro', aindaNaoAconteceu),
    observations: yup.string().defined().max(2000),
    storedQuantity: yup.number().nullable().defined().transform((v, o) => (o === '' ? null : v))
      .test('stored-positive', 'Quantidade deve ser maior que zero', (v) => v == null || v > 0),
    storedQuantityUnit: yup.string().defined().max(20, 'Máximo 20 caracteres'),
    transportedQuantity: yup.number().nullable().defined().transform((v, o) => (o === '' ? null : v))
      .test('transported-positive', 'Quantidade deve ser maior que zero', (v) => v == null || v > 0),
    transportedQuantityUnit: yup.string().defined().max(20, 'Máximo 20 caracteres'),
    riskArea: yup.string().defined().max(255, 'Máximo 255 caracteres'),
    storageType: yup.string().defined().max(255, 'Máximo 255 caracteres'),
    hazardousFraming: yup.boolean().defined()
      .test('framing-needs-annex', 'Informe o anexo da NR-16 que enquadra a atividade', function (v) {
        return v !== true || Boolean(this.parent.hazardousAnnex);
      }),
    hazardousAnnex: yup.string().defined().max(60, 'Máximo 60 caracteres')
      .test('annex-needs-framing', 'O anexo só vale com o enquadramento marcado', function (v) {
        return !v || this.parent.hazardousFraming === true;
      }),
    unhealthyFraming: yup.boolean().defined()
      .test('unhealthy-needs-degree', 'Informe o grau de insalubridade aplicado', function (v) {
        return v !== true || Boolean(this.parent.unhealthyDegreeApplied);
      }),
    unhealthyDegreeApplied: yup.string().defined()
      .test('degree-needs-framing', 'O grau só vale com o enquadramento marcado', function (v) {
        return !v || this.parent.unhealthyFraming === true;
      }),
  })).defined().default([]),
});

// ============================================================================
// COMPONENT
// ============================================================================

/** Fresh default form values (startDate must be a new Date per open). */
function buildDefaultValues(): StepperFormValues {
  return {
    exposureLevel: ExposureLevel.MEDIUM,
    startDate: new Date(),
    endDate: null,
    observations: '',
    riskMatrixType: null,
    riskProbability: null,
    riskSeverity: null,
    examTypes: [],
    reportTypes: [],
    existingAdministrativeMeasures: [],
    existingEngineeringMeasures: [],
    existingAdministrativeOther: '',
    existingEngineeringOther: '',
    existingEpiIds: [],
    proposedAdministrative: '',
    proposedEngineering: '',
    proposedEpi: '',
    measurements: [],
  };
}

export const RiskApplicationModal: React.FC<RiskApplicationModalProps> = (props) => {
  // Resolve display values
  const displayRiskName = props.riskName ?? 'Risco';
  const displayFactorName = props.riskFactorName ?? '';

  // Internal state
  const [activeStep, setActiveStep] = useState(0);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [epiOptions, setEpiOptions] = useState<ControlMeasuresEpiOption[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<MeasurementEquipmentOption[]>([]);
  const [defaultUnit, setDefaultUnit] = useState('');
  const [isHazardousRisk, setIsHazardousRisk] = useState(false);
  const [isUnhealthyRisk, setIsUnhealthyRisk] = useState(false);
  const [defaultUnhealthyDegree, setDefaultUnhealthyDegree] = useState('');

  // Form
  const methods = useForm<StepperFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: buildDefaultValues(),
    context: { isHazardousRisk },
  });
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = methods;

  // ============================================================================
  // POPULATE ON OPEN
  // ============================================================================

  // Selected EPIs (edit mode) whose protection is no longer active must still show
  // up — marked orphaned — so the operator can see and uncheck them; otherwise they
  // would be silently re-submitted with no visible checkbox.
  const orphanOptions = (
    selectedEpis: RiskApplicationExistingEpiRow[] | undefined,
    activeIds: Set<number>,
  ): ControlMeasuresEpiOption[] =>
    (selectedEpis ?? [])
      .filter((e) => e.epi && !activeIds.has(e.epiId))
      .map((e) => ({
        id: e.epi!.id,
        nome: e.epi!.nome,
        metodo: e.epi!.metodo,
        atenuacao: e.epi!.atenuacao ?? null,
        orphaned: true,
      }));

  const loadEpiOptions = (riskId: number, selectedEpis?: RiskApplicationExistingEpiRow[]) => {
    riskProtectionService.getProtections(riskId, { type: 'EPI' })
      .then((res) => {
        const active: ControlMeasuresEpiOption[] = res.protections
          .filter((p) => p.epi)
          .map((p) => ({
            id: p.epi!.id,
            nome: p.epi!.nome,
            metodo: p.epi!.metodo,
            atenuacao: p.epi!.atenuacao ?? null,
          }));
        const activeIds = new Set(active.map((o) => o.id));
        setEpiOptions([...active, ...orphanOptions(selectedEpis, activeIds)]);
      })
      .catch(() => setEpiOptions(orphanOptions(selectedEpis, new Set())));
  };

  // Selected equipments (edit mode) that are no longer active (soft-deleted) must
  // still show up — marked orphaned — so an existing row doesn't render blank.
  // Mirrors orphanOptions()/loadEpiOptions() above (EPI-órfão pattern, #221).
  const orphanEquipmentOptions = (
    selectedMeasurements: RiskApplicationMeasurementRow[] | undefined,
    activeIds: Set<number>,
  ): MeasurementEquipmentOption[] => {
    const byId = new Map<number, MeasurementEquipmentOption>();
    (selectedMeasurements ?? [])
      .filter((m) => m.equipment && !activeIds.has(m.equipment.id))
      .forEach((m) => {
        byId.set(m.equipment!.id, {
          id: m.equipment!.id,
          name: m.equipment!.name,
          serialNumber: m.equipment!.serialNumber ?? null,
          orphaned: true,
        });
      });
    return Array.from(byId.values());
  };

  const loadEquipmentOptions = (selectedMeasurements?: RiskApplicationMeasurementRow[]) => {
    measurementEquipmentService
      .getMeasurementEquipments({ status: MeasurementEquipmentStatus.ACTIVE, limit: 200 })
      .then((res) => {
        const active: MeasurementEquipmentOption[] = res.equipments.map((e) => ({
          id: e.id,
          name: e.name,
          serialNumber: e.serialNumber ?? null,
        }));
        const activeIds = new Set(active.map((o) => o.id));
        setEquipmentOptions([...active, ...orphanEquipmentOptions(selectedMeasurements, activeIds)]);
      })
      .catch(() => setEquipmentOptions(orphanEquipmentOptions(selectedMeasurements, new Set())));
  };

  useEffect(() => {
    if (!props.open) return;

    setActiveStep(0);
    setFetchError(null);
    setSubmitError(null);
    setEpiOptions([]);
    setEquipmentOptions([]);
    setDefaultUnit('');
    setIsHazardousRisk(false);
    setIsUnhealthyRisk(false);
    setDefaultUnhealthyDegree('');

    if (props.mode === 'edit' && props.applicationId != null) {
      const id = props.applicationId;
      setFetchLoading(true);
      riskApplicationService
        .getById(id)
        .then((app) => {
          if (!app) {
            setFetchError('Aplicacao nao encontrada');
            return;
          }
          // Matrix override: the application's own (complete) triple, falling back
          // to the parent risk's matrix — but ONLY when that source is complete
          // (type + probability + severity), so a partial matrix is never
          // pre-filled into a form that requires the full triple.
          const ownMatrix = pickCompleteMatrix(app);
          const matrix =
            ownMatrix.riskMatrixType != null ? ownMatrix : pickCompleteMatrix(app.risk);
          reset({
            ...buildDefaultValues(),
            exposureLevel: app.exposureLevel,
            startDate: app.startDate ? new Date(app.startDate) : null,
            endDate: app.endDate ? new Date(app.endDate) : null,
            observations: app.observations ?? '',
            ...matrix,
            examTypes: app.examTypes?.map((e) => e.examType) ?? [],
            reportTypes: app.reportTypes?.map((r) => r.reportType) ?? [],
            existingAdministrativeMeasures: app.existingMeasures?.filter((m) => m.category === 'ADMINISTRATIVE').map((m) => m.measure) ?? [],
            existingEngineeringMeasures: app.existingMeasures?.filter((m) => m.category === 'ENGINEERING').map((m) => m.measure) ?? [],
            existingAdministrativeOther: app.existingAdministrativeOther ?? '',
            existingEngineeringOther: app.existingEngineeringOther ?? '',
            existingEpiIds: app.existingEpis?.map((e) => e.epiId) ?? [],
            proposedAdministrative: app.proposedAdministrative ?? '',
            proposedEngineering: app.proposedEngineering ?? '',
            proposedEpi: app.proposedEpi ?? '',
            measurements: app.measurements?.map((m) => ({
              measurementEquipmentId: m.measurementEquipmentId ?? null,
              value: m.value != null ? Number(m.value) : null,
              unit: m.unit ?? '',
              // 'YYYY-MM-DD' + 'T00:00:00' força meia-noite LOCAL; sem isso o Date
              // interpretaria como UTC e o DatePicker exibiria um dia a menos em BRT.
              measuredAt: m.measuredAt ? new Date(`${m.measuredAt}T00:00:00`) : null,
              observations: m.observations ?? '',
              storedQuantity: m.storedQuantity != null ? Number(m.storedQuantity) : null,
              storedQuantityUnit: m.storedQuantityUnit ?? '',
              transportedQuantity: m.transportedQuantity != null ? Number(m.transportedQuantity) : null,
              transportedQuantityUnit: m.transportedQuantityUnit ?? '',
              riskArea: m.riskArea ?? '',
              storageType: m.storageType ?? '',
              hazardousFraming: m.hazardousFraming ?? false,
              hazardousAnnex: m.hazardousAnnex ?? '',
              unhealthyFraming: m.unhealthyFraming ?? false,
              unhealthyDegreeApplied: m.unhealthyDegreeApplied ?? '',
            })) ?? [],
          });
          loadEpiOptions(app.riskId, app.existingEpis);
          loadEquipmentOptions(app.measurements);
          setDefaultUnit(app.risk?.measurementUnit ?? '');
          setIsHazardousRisk(Boolean(app.risk?.hazardousAgent));
          setIsUnhealthyRisk(Boolean(app.risk?.unhealthyAgent));
          setDefaultUnhealthyDegree(app.risk?.unhealthyDegree ?? '');
        })
        .catch((err: unknown) => {
          setFetchError(err instanceof Error ? err.message : 'Erro ao carregar aplicacao');
        })
        .finally(() => setFetchLoading(false));
    } else if (props.riskId != null) {
      // Apply mode: fetch the parent risk, then reset once with its (complete)
      // matrix pre-filled so the operator sees and can override its classification.
      const riskId = props.riskId;
      setFetchLoading(true);
      occupationalRiskService
        .getOccupationalRiskById(riskId)
        .then((risk) => {
          reset({ ...buildDefaultValues(), ...pickCompleteMatrix(risk) });
          loadEpiOptions(riskId);
          loadEquipmentOptions();
          setDefaultUnit(risk.measurementUnit ?? '');
          setIsHazardousRisk(Boolean(risk.hazardousAgent));
          setIsUnhealthyRisk(Boolean(risk.unhealthyAgent));
          setDefaultUnhealthyDegree(risk.unhealthyDegree ?? '');
        })
        .catch((err: unknown) => {
          setFetchError(err instanceof Error ? err.message : 'Erro ao carregar matriz do risco');
          reset(buildDefaultValues());
        })
        .finally(() => setFetchLoading(false));
    } else {
      reset(buildDefaultValues());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // ============================================================================
  // NAVIGATION
  // ============================================================================

  const handleNext = async () => {
    // Validate step 1 fields before advancing from step 0
    if (activeStep === 0) {
      const valid = await trigger([
        'exposureLevel',
        'startDate',
        'endDate',
        'observations',
        'riskMatrixType',
        'riskProbability',
        'riskSeverity',
      ]);
      if (!valid) return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  // ============================================================================
  // SUBMIT
  // ============================================================================

  const orNull = (v: string) => (v.trim() ? v.trim() : null);

  /**
   * Os campos de periculosidade só viajam quando o risco é perigoso: o servidor recusa
   * preenchidos nos demais, e um resíduo de digitação barraria o salvamento inteiro.
   */
  const toMeasurementPayload = (m: MeasurementFormRow) => ({
    measurementEquipmentId: m.measurementEquipmentId,
    value: m.value,
    unit: m.unit.trim() ? m.unit.trim() : null,
    measuredAt: m.measuredAt ? m.measuredAt.toISOString().split('T')[0] : '',
    observations: m.observations.trim() ? m.observations.trim() : null,
    storedQuantity: isHazardousRisk ? m.storedQuantity : null,
    storedQuantityUnit: isHazardousRisk ? orNull(m.storedQuantityUnit) : null,
    transportedQuantity: isHazardousRisk ? m.transportedQuantity : null,
    transportedQuantityUnit: isHazardousRisk ? orNull(m.transportedQuantityUnit) : null,
    riskArea: isHazardousRisk ? orNull(m.riskArea) : null,
    storageType: isHazardousRisk ? orNull(m.storageType) : null,
    hazardousFraming: isHazardousRisk ? m.hazardousFraming : false,
    hazardousAnnex: isHazardousRisk ? orNull(m.hazardousAnnex) : null,
    unhealthyFraming: isUnhealthyRisk ? m.unhealthyFraming : false,
    unhealthyDegreeApplied: isUnhealthyRisk ? orNull(m.unhealthyDegreeApplied) : null,
  });

  const onSubmit = async (data: StepperFormValues) => {
    setSubmitError(null);

    const startDateStr = data.startDate?.toISOString().split('T')[0] ?? new Date().toISOString().split('T')[0];
    const endDateStr = data.endDate?.toISOString().split('T')[0] ?? null;

    const examTypes = data.examTypes as ExamType[];
    const reportTypes = data.reportTypes as ReportType[];

    try {
      if (props.mode === 'apply' && props.riskId != null) {
        await riskApplicationService.apply({
          riskId: props.riskId,
          scope: props.scope,
          scopeId: props.scopeId,
          exposureLevel: data.exposureLevel,
          startDate: startDateStr,
          endDate: endDateStr,
          observations: data.observations ?? null,
          riskMatrixType: data.riskMatrixType,
          riskProbability: data.riskProbability,
          riskSeverity: data.riskSeverity,
          examTypes,
          reportTypes,
          existingAdministrativeMeasures: data.existingAdministrativeMeasures,
          existingEngineeringMeasures: data.existingEngineeringMeasures,
          existingAdministrativeOther: orNull(data.existingAdministrativeOther),
          existingEngineeringOther: orNull(data.existingEngineeringOther),
          existingEpiIds: data.existingEpiIds,
          proposedAdministrative: orNull(data.proposedAdministrative),
          proposedEngineering: orNull(data.proposedEngineering),
          proposedEpi: orNull(data.proposedEpi),
          measurements: data.measurements.map(toMeasurementPayload),
        });
      } else if (props.mode === 'edit' && props.applicationId != null) {
        await riskApplicationService.update(props.applicationId, {
          exposureLevel: data.exposureLevel,
          startDate: startDateStr,
          endDate: endDateStr,
          observations: data.observations ?? null,
          riskMatrixType: data.riskMatrixType,
          riskProbability: data.riskProbability,
          riskSeverity: data.riskSeverity,
          examTypes,
          reportTypes,
          existingAdministrativeMeasures: data.existingAdministrativeMeasures,
          existingEngineeringMeasures: data.existingEngineeringMeasures,
          existingAdministrativeOther: orNull(data.existingAdministrativeOther),
          existingEngineeringOther: orNull(data.existingEngineeringOther),
          existingEpiIds: data.existingEpiIds,
          proposedAdministrative: orNull(data.proposedAdministrative),
          proposedEngineering: orNull(data.proposedEngineering),
          proposedEpi: orNull(data.proposedEpi),
          measurements: data.measurements.map(toMeasurementPayload),
        });
      }
      props.onSaved();
      props.onClose();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao salvar aplicacao');
    }
  };

  const isBusy = isSubmitting || fetchLoading;
  const shownError = submitError ?? fetchError;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Dialog
      open={props.open}
      onClose={isBusy ? undefined : props.onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {props.mode === 'apply' ? 'Aplicar Risco' : 'Editar Aplicacao'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {displayRiskName}
          {displayFactorName && (
            <Chip label={displayFactorName} size="small" sx={{ ml: 1 }} variant="outlined" />
          )}
        </Typography>
      </DialogTitle>

      <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Box sx={{ mb: 3 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {shownError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setSubmitError(null); setFetchError(null); }}>
                {shownError}
              </Alert>
            )}

            {fetchLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {activeStep === 0 && (
                  <>
                    <Step1Exposure
                      control={control}
                      errors={errors}
                      disabled={isBusy}
                    />
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Classificacao de Risco (Matriz)
                    </Typography>
                    <RiskMatrixStep />
                  </>
                )}
                {activeStep === 1 && (
                  <Step2ExamTypes control={control} disabled={isBusy} />
                )}
                {activeStep === 2 && (
                  <Step3Reports control={control} disabled={isBusy} />
                )}
                {activeStep === 3 && (
                  <ControlMeasuresStep epiOptions={epiOptions} disabled={isBusy} />
                )}
                {activeStep === 4 && (
                  <MeasurementsStep
                    equipmentOptions={equipmentOptions}
                    defaultUnit={defaultUnit}
                    disabled={isBusy}
                    hazardous={isHazardousRisk}
                    unhealthy={isUnhealthyRisk}
                    defaultUnhealthyDegree={defaultUnhealthyDegree}
                  />
                )}
              </>
            )}
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button type="button" onClick={props.onClose} disabled={isBusy} color="inherit">
            Cancelar
          </Button>
          <Box sx={{ flex: 1 }} />
          {activeStep > 0 && (
            <Button type="button" onClick={handleBack} disabled={isBusy}>
              Voltar
            </Button>
          )}
          {activeStep < STEPS.length - 1 ? (
            <Button key="next" type="button" variant="contained" onClick={handleNext} disabled={isBusy || fetchLoading}>
              Proximo
            </Button>
          ) : (
            <Button
              key="save"
              type="submit"
              variant="contained"
              disabled={isBusy}
              startIcon={isBusy && <CircularProgress size={16} />}
            >
              Salvar
            </Button>
          )}
        </DialogActions>
      </form>
      </FormProvider>
    </Dialog>
  );
};

export default RiskApplicationModal;
